import React, { useMemo, useState } from 'react';
import { GameState } from '../../domain/entities';
import { ASSISTANT_MANAGER_LEVELS } from '../../domain/constants';
import { useAssistantManagerManagement } from '../../application/hooks/management/useAssistantManagerManagement';
import { useGameActions } from '../../contexts/GameActionsContext';
import { soundService } from '../../services/SoundService';
import { useToast } from '../../contexts/ToastContext';
import { getAssistantManagerEmoji } from '../../utils/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faDumbbell, faAmbulance, faChartArea, faMoneyBill, faBullseye, faLightbulb, faEnvelope, faClipboard, faArrowUp, faArrowDown, faChalkboardUser, faExclamationTriangle, faCircleInfo, faUsers, faExchangeAlt, faCalendarAlt, faBatteryEmpty, faBatteryQuarter, faShieldHalved, faGun, faFire, faFaceFlushed, faFaceSadTear, faCoins, faMoneyBillWave, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { Modal } from '../shared/Modal';
import { AssistantManagerHelpModal } from '../shared/AssistantManagerHelpModal';
import { TransferSuggestionsPanel } from '../shared/TransferSuggestionsPanel';

interface AssistantScreenProps {
    gameState: GameState;
    onUpdateGameState: (state: Partial<GameState>) => void;
    season?: number;
    week?: number;
}

export const AssistantScreen: React.FC<AssistantScreenProps> = ({ gameState, onUpdateGameState, season, week }) => {
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
    const { addToast } = useToast();
    const gameActions = useGameActions();

    const team = useMemo(() => {
        return gameState.leagues[gameState.playerLeagueName]?.teams.find(t => t.name === gameState.playerTeamName);
    }, [gameState, gameState.leagues, gameState.playerLeagueName, gameState.playerTeamName]);

    const assistantManagerMgmt = useAssistantManagerManagement(gameState);
    const managerEmoji = getAssistantManagerEmoji(gameState.gameType);
    const currentLevel = useMemo(() => team?.assistantManager.level || 1, [team?.assistantManager.level]);
    const managerLevel = ASSISTANT_MANAGER_LEVELS[currentLevel - 1];

    if (!team) return null;

    const handleAcceptSuggestion = async (suggestionId: string) => {
        soundService.playSuccess();
        const result = await gameActions.acceptTransferSuggestion(suggestionId);
        addToast(result.message, result.success ? 'success' : 'error');
    };

    const handleRejectSuggestion = async (suggestionId: string) => {
        soundService.playClick();
        const result = await gameActions.rejectTransferSuggestion(suggestionId);
        addToast(result.message, result.success ? 'success' : 'error');
    };

    const handleConfirmUpgrade = () => {
        soundService.playSuccess();
        const result = assistantManagerMgmt.upgradeAssistantManager(team.name);
        if (result.success) {
            onUpdateGameState({
                leagues: result.updatedGameState!.leagues,
                assistantManagerBriefingHistory: result.updatedGameState!.assistantManagerBriefingHistory,
                news: result.updatedGameState!.news
            });
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setUpgradeModalOpen(false);
    };

    const handleConfirmDowngrade = () => {
        soundService.playSuccess();
        const result = assistantManagerMgmt.downgradeAssistantManager(team.name);
        if (result.success) {
            onUpdateGameState({
                leagues: result.updatedGameState!.leagues,
                assistantManagerBriefingHistory: result.updatedGameState!.assistantManagerBriefingHistory,
                news: result.updatedGameState!.news
            });
            addToast(result.message, 'success');
        } else {
            soundService.playError();
            addToast(result.message, 'error');
        }
        setDowngradeModalOpen(false);
    };

    // Get briefing history (most recent first)
    const briefingHistory = useMemo(() => {
        const history = gameState.assistantManagerBriefingHistory || [];
        return [...history].reverse();
    }, [gameState.assistantManagerBriefingHistory]);

    // If season and week are provided (clicked from news item), find that specific briefing; otherwise show latest
    const selectedBriefing = useMemo(() => {
        if (season !== undefined && week !== undefined) {
            // Find the briefing matching the requested season and week
            return gameState.assistantManagerBriefingHistory?.find(b => b.season === season && b.week === week) || null;
        }
        // Fall back to latest briefing
        return briefingHistory.length > 0 ? briefingHistory[0] : null;
    }, [season, week, gameState.assistantManagerBriefingHistory, briefingHistory]);

    const latestBriefing = selectedBriefing;

    return (
        <>
            <AdPlaceholder />
            <div className="main-content assistant-screen">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1><FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: '8px', color: 'var(--accent-color)' }} />Assistant Manager</h1>
                    <button
                        className="button"
                        onClick={() => setIsHelpOpen(true)}
                        style={{ padding: '8px 12px', fontSize: '0.9em' }}
                        title="Help"
                    >
                        Help
                    </button>
                </div>
                <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                    Your Assistant Manager provides weekly tactical briefings and can be upgraded to improve recommendations.
                </p>

                {/* Current Manager Info */}
                <CollapsibleCard
                    cardId="assistant-manager-info"
                    title={`${managerEmoji} ${managerLevel.name} - Level ${currentLevel}`}
                    defaultCollapsed={false}
                >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Accuracy</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>{Math.round(managerLevel.accuracy * 100)}%</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>How accurate tactics recommendations are</div>
                        </div>
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                            <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '4px' }}>Weekly Cost</div>
                            <div style={{ fontSize: '1.4em', fontWeight: 'bold' }}>£{managerLevel.weeklyWages.toLocaleString()}</div>
                            <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Wages deducted each week</div>
                        </div>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'rgba(0, 255, 155, 0.1)', borderRadius: '4px', marginBottom: '16px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>What they do:</p>
                        <ul style={{ margin: '0', paddingLeft: '20px' }}>
                            <li>Provide weekly tactical briefings with squad & opposition analysis</li>
                            <li>Offer transfer recommendations for squad improvements</li>
                            <li>Suggest tactical setups via Auto-Setup button on Tactics screen</li>
                            <li>Higher levels = more accurate recommendations & analysis</li>
                        </ul>
                    </div>

                    {/* Upgrade/Downgrade Controls */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        {currentLevel < ASSISTANT_MANAGER_LEVELS.length && (
                            <button
                                className="button"
                                onClick={() => setUpgradeModalOpen(true)}
                                style={{ flex: 1 }}
                            >
                                <FontAwesomeIcon icon={faArrowUp} style={{ marginRight: '6px' }} />Upgrade to {ASSISTANT_MANAGER_LEVELS[currentLevel]?.name || 'Next Level'}
                            </button>
                        )}
                        {currentLevel > 1 && (
                            <button
                                className="button"
                                onClick={() => setDowngradeModalOpen(true)}
                                style={{ flex: 1 }}
                            >
                                <FontAwesomeIcon icon={faArrowDown} style={{ marginRight: '6px' }} />Downgrade
                            </button>
                        )}
                    </div>

                    {/* Upgrade/Downgrade Cost Info */}
                    {currentLevel < ASSISTANT_MANAGER_LEVELS.length && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em', marginBottom: '12px' }}>
                            <strong>Upgrade Cost:</strong> £{ASSISTANT_MANAGER_LEVELS[currentLevel].upgradeCost.toLocaleString()}
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--accent-text-color)' }}>
                                Upgrading to {ASSISTANT_MANAGER_LEVELS[currentLevel].name} increases accuracy to {Math.round(ASSISTANT_MANAGER_LEVELS[currentLevel].accuracy * 100)}%
                            </div>
                        </div>
                    )}
                    {currentLevel > 1 && (
                        <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px', fontSize: '0.9em' }}>
                            <strong>💰 Save Weekly Wages:</strong> Downgrading reduces your weekly wages
                            <div style={{ marginTop: '8px', fontSize: '0.85em', color: 'var(--positive-color)' }}>
                                Downgrading to {ASSISTANT_MANAGER_LEVELS[currentLevel - 2]?.name} saves £{(managerLevel.weeklyWages - ASSISTANT_MANAGER_LEVELS[currentLevel - 2]?.weeklyWages).toLocaleString()}/wk
                            </div>
                        </div>
                    )}
                </CollapsibleCard>

                {/* Transfer Suggestions */}
                <TransferSuggestionsPanel
                    gameState={gameState}
                    onAcceptSuggestion={handleAcceptSuggestion}
                    onRejectSuggestion={handleRejectSuggestion}
                />

                {/* Latest Briefing */}
                {latestBriefing && (
                    <CollapsibleCard
                        cardId="latest-briefing"
                        title={<><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '6px', color: 'var(--accent-color)' }} />Latest Briefing (Week {latestBriefing.week}, Season {latestBriefing.season})</>}
                        defaultCollapsed={false}
                    >
                        <BriefingDisplay briefing={latestBriefing} managerEmoji={managerEmoji} />
                    </CollapsibleCard>
                )}

                {/* Briefing History */}
                {briefingHistory.length > 1 && (
                    <CollapsibleCard
                        cardId="briefing-history"
                        title={<><FontAwesomeIcon icon={faClipboard} style={{ marginRight: '6px', color: 'var(--info-color)' }} />Briefing History ({briefingHistory.length} total)</>}
                        defaultCollapsed={true}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {briefingHistory.slice(1).map((briefing, idx) => (
                                <CollapsibleCard
                                    key={`briefing-${briefing.season}-${briefing.week}`}
                                    cardId={`briefing-${briefing.season}-${briefing.week}`}
                                    title={`Week ${briefing.week}, Season ${briefing.season}`}
                                    defaultCollapsed={true}
                                >
                                    <BriefingDisplay briefing={briefing} managerEmoji={managerEmoji} />
                                </CollapsibleCard>
                            ))}
                        </div>
                    </CollapsibleCard>
                )}

                {/* No Briefings Yet */}
                {briefingHistory.length === 0 && (
                    <div style={{ padding: '24px', textAlign: 'center', opacity: 0.7 }}>
                        <p>No briefings yet. Weekly briefings will appear here each week.</p>
                    </div>
                )}

                <AssistantManagerHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
            </div>

            <Modal isOpen={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)}>
                <h2>Confirm Assistant Manager Upgrade</h2>
                <p>Are you sure you want to upgrade your Assistant Manager to Level {currentLevel + 1} ({ASSISTANT_MANAGER_LEVELS[currentLevel]?.name})?</p>
                <p><strong>Cost:</strong> £{ASSISTANT_MANAGER_LEVELS[currentLevel]?.upgradeCost.toLocaleString()}</p>
                <p className="text-secondary">A higher-level assistant manager will provide more accurate tactical recommendations and analysis.</p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setUpgradeModalOpen(false)}>Cancel</button>
                    <button className="button primary" onClick={handleConfirmUpgrade}>Confirm Upgrade</button>
                </div>
            </Modal>

            <Modal isOpen={downgradeModalOpen} onClose={() => setDowngradeModalOpen(false)}>
                <h2>Confirm Assistant Manager Downgrade</h2>
                <p>Are you sure you want to downgrade your Assistant Manager?</p>
                <p><strong>Severance Cost:</strong> £{(ASSISTANT_MANAGER_LEVELS[currentLevel - 1]?.weeklyWages * 8).toLocaleString()}</p>
                <p className="text-secondary">You will need to pay 8 weeks of severance to your current Assistant Manager.</p>
                <div className="modal-actions">
                    <button className="button" onClick={() => setDowngradeModalOpen(false)}>Cancel</button>
                    <button className="button primary" onClick={handleConfirmDowngrade}>Confirm Downgrade</button>
                </div>
            </Modal>
        </>
    );
};

interface BriefingDisplayProps {
    briefing: {
        data: {
            status: string;
            keyIssues?: Array<{ issue: string; severity: 'critical' | 'warning' | 'info'; icon: string }>;
            squadCondition: string;
            injuryReport: string;
            formWatch: string;
            topFormers?: string[];
            playerAlerts?: Array<{ name: string; alert: string; type: 'morale' | 'fitness' | 'form' | 'injury' }>;
            finances: string;
            tacticalRecommendations?: Array<{ recommendation: string; reason: string }>;
            transferTargets?: Array<{ position: string; reasoning: string }>;
            upcomingFixtures?: { difficulty: 'easy' | 'moderate' | 'hard'; summary: string };
            advice: string;
        };
    };
    managerEmoji: string;
}

const getSeverityColor = (severity: 'critical' | 'warning' | 'info'): string => {
    switch (severity) {
        case 'critical':
            return 'var(--negative-color)';
        case 'warning':
            return 'var(--warning-color)';
        case 'info':
        default:
            return 'var(--info-color)';
    }
};

const getAlertIconColor = (type: 'morale' | 'fitness' | 'form' | 'injury'): string => {
    switch (type) {
        case 'morale':
            return 'var(--negative-color)';
        case 'fitness':
            return 'var(--warning-color)';
        case 'form':
            return 'var(--info-color)';
        case 'injury':
            return 'var(--negative-color)';
        default:
            return 'var(--accent-text-color)';
    }
};

const getAlertIcon = (type: 'morale' | 'fitness' | 'form' | 'injury') => {
    switch (type) {
        case 'morale':
            return faFaceSadTear;
        case 'fitness':
            return faBatteryEmpty;
        case 'form':
            return faFire;
        case 'injury':
            return faAmbulance;
        default:
            return faCircleInfo;
    }
};

const BriefingDisplay: React.FC<BriefingDisplayProps> = ({ briefing, managerEmoji }) => {
    const { data } = briefing;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Status */}
            <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faChartLine} style={{ marginRight: '6px', color: 'var(--info-color)' }} />League Status
                </div>
                <div>{data.status}</div>
            </div>

            {/* Key Issues */}
            {data.keyIssues && data.keyIssues.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: '6px', color: 'var(--negative-color)' }} />Key Issues
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '0', listStyle: 'none' }}>
                        {data.keyIssues.map((issue, idx) => (
                            <li key={idx} style={{ marginBottom: idx < data.keyIssues!.length - 1 ? '8px' : '0', paddingLeft: '0', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <span style={{ color: getSeverityColor(issue.severity), marginTop: '2px' }}>
                                    <i className={`fas fa-${issue.icon}`} style={{ minWidth: '14px' }} />
                                </span>
                                <span>{issue.issue}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Player Alerts */}
            {data.playerAlerts && data.playerAlerts.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px', color: 'var(--warning-color)' }} />Player Alerts
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '0', listStyle: 'none' }}>
                        {data.playerAlerts.map((alert, idx) => (
                            <li key={idx} style={{ marginBottom: idx < data.playerAlerts!.length - 1 ? '8px' : '0', paddingLeft: '0', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                <span style={{ color: getAlertIconColor(alert.type), marginTop: '2px' }}>
                                    <FontAwesomeIcon icon={getAlertIcon(alert.type)} style={{ minWidth: '14px' }} />
                                </span>
                                <span><strong>{alert.name}:</strong> {alert.alert}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Squad Condition */}
            <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faDumbbell} style={{ marginRight: '6px', color: 'var(--positive-color)' }} />Squad Condition
                </div>
                <div>{data.squadCondition}</div>
            </div>

            {/* Injury Report */}
            <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faAmbulance} style={{ marginRight: '6px', color: 'var(--negative-color)' }} />Injury Report
                </div>
                <div>{data.injuryReport}</div>
            </div>

            {/* Form Watch */}
            <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faChartArea} style={{ marginRight: '6px', color: 'var(--accent-color)' }} />Form Watch
                </div>
                <div>{data.formWatch}</div>
                {data.topFormers && data.topFormers.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '0.9em' }}>
                        <strong>Top performers:</strong> {data.topFormers.join(', ')}
                    </div>
                )}
            </div>

            {/* Tactical Recommendations */}
            {data.tacticalRecommendations && data.tacticalRecommendations.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: '6px', color: 'var(--info-color)' }} />Tactical Recommendations
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '0', listStyle: 'none' }}>
                        {data.tacticalRecommendations.map((rec, idx) => (
                            <li key={idx} style={{ marginBottom: idx < data.tacticalRecommendations!.length - 1 ? '8px' : '0', paddingLeft: '0' }}>
                                <div style={{ fontWeight: '500' }}>{rec.recommendation}</div>
                                <div style={{ fontSize: '0.85em', marginTop: '2px', color: 'var(--accent-text-color)' }}>{rec.reason}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Transfer Targets */}
            {data.transferTargets && data.transferTargets.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faExchangeAlt} style={{ marginRight: '6px', color: 'var(--accent-color)' }} />Transfer Targets
                    </div>
                    <ul style={{ margin: '0', paddingLeft: '0', listStyle: 'none' }}>
                        {data.transferTargets.map((target, idx) => (
                            <li key={idx} style={{ marginBottom: idx < data.transferTargets!.length - 1 ? '8px' : '0', paddingLeft: '0' }}>
                                <div style={{ fontWeight: '500' }}>{target.position}</div>
                                <div style={{ fontSize: '0.85em', marginTop: '2px', color: 'var(--accent-text-color)' }}>{target.reasoning}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Upcoming Fixtures */}
            {data.upcomingFixtures && (
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px', color: 'var(--info-color)' }} />Upcoming Fixtures
                    </div>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                            padding: '6px 10px',
                            backgroundColor: data.upcomingFixtures.difficulty === 'easy' ? 'rgba(0, 255, 100, 0.2)' :
                                           data.upcomingFixtures.difficulty === 'moderate' ? 'rgba(255, 200, 0, 0.2)' :
                                           'rgba(255, 0, 0, 0.2)',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            fontWeight: 'bold',
                            fontSize: '0.85em',
                            color: data.upcomingFixtures.difficulty === 'easy' ? 'var(--positive-color)' :
                                   data.upcomingFixtures.difficulty === 'moderate' ? 'var(--warning-color)' :
                                   'var(--negative-color)'
                        }}>
                            {data.upcomingFixtures.difficulty.charAt(0).toUpperCase() + data.upcomingFixtures.difficulty.slice(1)}
                        </div>
                        <div style={{ flex: 1 }}>{data.upcomingFixtures.summary}</div>
                    </div>
                </div>
            )}

            {/* Finances */}
            <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '6px', color: 'var(--warning-color)' }} />Finances
                </div>
                <div>{data.finances}</div>
            </div>

            {/* Manager Advice */}
            <div style={{ padding: '12px', backgroundColor: 'rgba(0, 255, 155, 0.1)', borderRadius: '4px' }}>
                <div style={{ fontSize: '0.9em', color: 'var(--accent-text-color)', marginBottom: '8px', fontWeight: 'bold' }}>
                    <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '6px', color: 'var(--warning-color)' }} />Manager's Advice
                </div>
                <div style={{ fontStyle: 'italic' }}>{data.advice}</div>
            </div>
        </div>
    );
};
